import { z } from 'zod'
import { uuid } from './primitives.ts'

/** M24-F: the forecast (docs/M24.md B, D, E, rules 1, 2, 4, 6, 8; ADR-125; migration `20261220100300_forecast.sql`, whose header holds every rule). A forecast
 *  is a file the club's server fetched (DWD MOSMIX_L for one point, CC BY 4.0), read as untrusted data by the job's reader (`packages/jobs/src/weather/`),
 *  checked again and converted by the database, and kept in its OWN schema `weather` — never in the notebook, never mixed with a reading, and nothing here
 *  decides anything (no work window, no all-clear, no alert).
 *  - `forecastFileV1`: what the kind's reader sends its door `jobs.ingest_forecast` (the twin of `ref.connector_kind.input_schema` for `dwd_mosmix_l`,
 *    registered in CONNECTOR_INPUT_SCHEMAS): the file's issue as it wrote it, the provider's codes, the provider's units — the job converts nothing.
 *  - `forecastIngestV1`: the payload of `forecast.ingest@1`, the first command of kind `system` (never from a device: the dispatcher refuses it,
 *    COMMAND_SYSTEM_ONLY): the PREPARED issue (`private.forecast_prepare`), every series in its element's canonical unit, converted by `ref.convert`.
 *  - `ForecastView`: what `api.forecast_view(p_org, p_site)` answers (pgTAP 91 pins it: change them together).
 *  `FORECAST_ELEMENTS` and `CONNECTOR_ELEMENTS` are the twins of `ref.forecast_element` and `ref.connector_element` (`npm run test:contracts` compares them row
 *  by row); `packages/jobs/src/weather/definitions.test.ts` holds each mapping's `unit_given` to the pinned extract of DWD's element definitions. */

/** An instant as the file and the database write it (ISO 8601 with a zone; up to six decimals of a second). */
export const ISO_INSTANT_PATTERN = '^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{1,6})?(Z|[+-]\\d{2}:\\d{2})$'
const isoInstant = z.string().regex(new RegExp(ISO_INSTANT_PATTERN))
/** The hex SHA-256 of the original file (computed by the reader from the bytes it was given; the door finds the kept original by it). */
export const SHA256_HEX_PATTERN = /^[0-9a-f]{64}$/
const sha256Hex = z.string().regex(SHA256_HEX_PATTERN)
/** A provider's own element code ("TTT", "RR1c", "wwT"): a ROW VALUE of `ref.connector_element`, never a column (non-negotiable 5). */
export const PROVIDER_CODE_PATTERN = /^[A-Za-z0-9_]{1,16}$/
/** The largest number of time steps an issue may have (MOSMIX_L has 247 today: never hard-code it, the issue says how many). */
export const FORECAST_MAX_STEPS = 1000
/** The only step the database accepts today (MOSMIX_L is hourly): FORECAST_STEPS_INVALID otherwise. */
export const FORECAST_STEP_MINUTES = 60
/** An issue more than this many minutes ahead of the server's clock is refused (FORECAST_ISSUE_IN_FUTURE). */
export const FORECAST_ISSUE_FUTURE_MINUTES = 5

const models = z.array(z.strictObject({ name: z.string().min(1).max(60), reference_time: isoInstant })).max(10)
const vals = z.array(z.number().nullable()).min(1).max(FORECAST_MAX_STEPS)

/** The reader's file payload for `dwd_mosmix_l` (docs/M24.md E step 5): what the KML says, nothing converted, no unit ("-" is null). */
export const forecastFileV1 = z.strictObject({
  raw_sha256: sha256Hex,
  issuer: z.string().min(1).max(120),
  product: z.string().min(1).max(60),
  generating_process: z.string().min(1).max(120),
  issued_at: isoInstant,
  models,
  first_step_at: isoInstant,
  step_minutes: z.number().int().min(1).max(1440),
  step_count: z.number().int().min(1).max(FORECAST_MAX_STEPS),
  point: z.strictObject({
    code: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    name: z.string().min(1).max(120),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    height_m: z.number().min(-500).max(9000),
  }),
  series: z.array(z.strictObject({ provider_code: z.string().regex(PROVIDER_CODE_PATTERN), vals })).min(1).max(100),
})
export type ForecastFileV1 = z.infer<typeof forecastFileV1>

const words = z.strictObject({ fr: z.string().min(1).max(200), en: z.string().min(1).max(200) })

/** forecast.ingest@1 (kind `system`): the prepared issue, each series in its element's canonical unit at `ref.convert`'s six places. */
export const forecastIngestV1 = z.strictObject({
  connector_id: uuid,
  raw_payload_id: uuid,
  raw_sha256: sha256Hex,
  provider: z.string().min(1).max(120),
  issuer: z.string().min(1).max(120),
  product: z.string().min(1).max(60),
  generating_process: z.string().min(1).max(120),
  models,
  point_code: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  point_name: z.string().min(1).max(120),
  point_lat: z.number().min(-90).max(90),
  point_lon: z.number().min(-180).max(180),
  point_height_m: z.number().min(-500).max(9000),
  distance_km: z.number().min(0).max(20100).nullable(),
  bearing_deg: z.number().int().min(0).max(359).nullable(),
  issued_at: isoInstant,
  first_step_at: isoInstant,
  step_minutes: z.number().int().min(1).max(1440),
  step_count: z.number().int().min(1).max(FORECAST_MAX_STEPS),
  attribution: words,
  licence: z.string().min(1).max(60).nullable(),
  licence_url: z.string().regex(/^https:\/\/[A-Za-z0-9./_~-]{1,200}$/).nullable(),
  series: z.array(z.strictObject({ element_code: z.string().regex(/^[a-z][a-z0-9_]{2,40}$/), vals })).min(1).max(100),
})
export type ForecastIngestV1 = z.infer<typeof forecastIngestV1>

/** The thirteen elements (`ref.forecast_element`; the order is `sort`). `accumulation_hours`: null for an instant, else the period that ENDS at the
 *  valid time (MOSMIX's convention: a 1-hour value at t covers (t − 1 h, t]). `min` / `max`: physical bounds on the canonical value (one value outside
 *  refuses the whole issue, FORECAST_VALUE_OUT_OF_RANGE; never clamped). `decimals`: display only. */
export const FORECAST_ELEMENT_CODES = [
  'air_temperature', 'dew_point', 'air_temperature_min_12h', 'air_temperature_max_12h', 'ground_temperature_min_12h', 'rain_1h', 'rain_probability_1h',
  'thunder_probability_1h', 'wind_speed', 'wind_gust_1h', 'wind_direction', 'global_radiation_1h', 'potential_evaporation_24h',
] as const
export type ForecastElementCode = (typeof FORECAST_ELEMENT_CODES)[number]
export interface ForecastElementDef {
  code: ForecastElementCode; unit: string; accumulation_hours: number | null; min: string; max: string; decimals: number; sort: number
  label: { fr: string; en: string }
}
export const FORECAST_ELEMENTS: readonly ForecastElementDef[] = [
  { code: 'air_temperature', unit: 'degC', accumulation_hours: null, min: '-80', max: '60', decimals: 1, sort: 10, label: { fr: 'Température de l\'air (2 m)', en: 'Air temperature (2 m)' } },
  { code: 'dew_point', unit: 'degC', accumulation_hours: null, min: '-90', max: '60', decimals: 1, sort: 20, label: { fr: 'Point de rosée (2 m)', en: 'Dew point (2 m)' } },
  { code: 'air_temperature_min_12h', unit: 'degC', accumulation_hours: 12, min: '-80', max: '60', decimals: 1, sort: 30, label: { fr: 'Température minimale sur 12 h (2 m)', en: 'Minimum temperature over 12 h (2 m)' } },
  { code: 'air_temperature_max_12h', unit: 'degC', accumulation_hours: 12, min: '-80', max: '60', decimals: 1, sort: 40, label: { fr: 'Température maximale sur 12 h (2 m)', en: 'Maximum temperature over 12 h (2 m)' } },
  { code: 'ground_temperature_min_12h', unit: 'degC', accumulation_hours: 12, min: '-90', max: '80', decimals: 1, sort: 50, label: { fr: 'Minimum au sol (5 cm) sur 12 h', en: 'Ground minimum (5 cm) over 12 h' } },
  { code: 'rain_1h', unit: 'mm', accumulation_hours: 1, min: '0', max: '500', decimals: 1, sort: 60, label: { fr: 'Précipitations sur 1 h (eau)', en: 'Precipitation over 1 h (water)' } },   // M24 review: RR1c is the TOTAL precipitation (snow and sleet as water)
  { code: 'rain_probability_1h', unit: 'pct', accumulation_hours: 1, min: '0', max: '100', decimals: 0, sort: 70, label: { fr: 'Chance de précipitations dans l\'heure', en: 'Chance of precipitation within the hour' } },   // wwP: any precipitation
  { code: 'thunder_probability_1h', unit: 'pct', accumulation_hours: 1, min: '0', max: '100', decimals: 0, sort: 80, label: { fr: 'Chance d\'orage dans l\'heure', en: 'Chance of a thunderstorm within the hour' } },
  { code: 'wind_speed', unit: 'km_h', accumulation_hours: null, min: '0', max: '500', decimals: 0, sort: 90, label: { fr: 'Vent moyen (10 m)', en: 'Mean wind (10 m)' } },
  { code: 'wind_gust_1h', unit: 'km_h', accumulation_hours: 1, min: '0', max: '500', decimals: 0, sort: 100, label: { fr: 'Rafale maximale sur 1 h', en: 'Highest gust over 1 h' } },
  { code: 'wind_direction', unit: 'deg', accumulation_hours: null, min: '0', max: '360', decimals: 0, sort: 110, label: { fr: 'Direction du vent', en: 'Wind direction' } },
  { code: 'global_radiation_1h', unit: 'MJ_m2', accumulation_hours: 1, min: '0', max: '5', decimals: 2, sort: 120, label: { fr: 'Rayonnement global sur 1 h', en: 'Global radiation over 1 h' } },
  { code: 'potential_evaporation_24h', unit: 'mm', accumulation_hours: 24, min: '0', max: '30', decimals: 1, sort: 130, label: { fr: 'Évapotranspiration potentielle du service sur 24 h', en: 'The service\'s potential evapotranspiration over 24 h' } },
]

/** Where the provider's units were read (the KML carries none): DWD's element definitions, the live blocks only (CC BY 4.0). */
export const MOSMIX_DEFINITION_REF = 'Deutscher Wetterdienst, MetElementDefinition.xml, Last-Modified 2024-10-22'
/** The mapping of a kind's provider codes to the elements (`ref.connector_element`; UNIQUE (kind, provider_code) and (kind, element_code)). `unit_given` is a
 *  `ref.unit` code: the database converts each value with `ref.convert(value, unit_given, element.unit)`. 1 kg/m² of water is 1 mm (RR1c, PEvap). */
export interface ConnectorElementDef { kind: string; provider_code: string; element_code: ForecastElementCode; unit_given: string; definition_ref: string }
const mosmix = (provider_code: string, element_code: ForecastElementCode, unit_given: string): ConnectorElementDef =>
  ({ kind: 'dwd_mosmix_l', provider_code, element_code, unit_given, definition_ref: MOSMIX_DEFINITION_REF })
export const CONNECTOR_ELEMENTS: readonly ConnectorElementDef[] = [
  mosmix('TTT', 'air_temperature', 'K'),
  mosmix('Td', 'dew_point', 'K'),
  mosmix('TN', 'air_temperature_min_12h', 'K'),
  mosmix('TX', 'air_temperature_max_12h', 'K'),
  mosmix('TG', 'ground_temperature_min_12h', 'K'),
  mosmix('RR1c', 'rain_1h', 'mm'),
  mosmix('wwP', 'rain_probability_1h', 'pct'),
  mosmix('wwT', 'thunder_probability_1h', 'pct'),
  mosmix('FF', 'wind_speed', 'm_s'),
  mosmix('FX1', 'wind_gust_1h', 'm_s'),
  mosmix('DD', 'wind_direction', 'deg'),
  mosmix('Rad1h', 'global_radiation_1h', 'kJ_m2'),
  mosmix('PEvap', 'potential_evaporation_24h', 'mm'),
]

export interface ForecastWords { fr: string; en: string }
/** One element of the newest issue: every element the kind maps, in `sort` order; `vals` has `step_count` entries (the valid time of entry i is
 *  `first_step_at + i × step_minutes`), null where the file said "-"; `provided` is false when not one value was given for this point (shown "non fourni
 *  pour ce point", never 0, never hidden). */
export interface ForecastElementSeries {
  code: ForecastElementCode; label: ForecastWords; unit: string; accumulation_hours: number | null; decimals: number; sort: number
  provided: boolean; vals: Array<number | null>
}
/** What `api.forecast_view(p_org, p_site)` answers (forecast.read, online): null when the site has no open forecast connector or no issue yet. */
export interface ForecastView {
  site_id: string; as_of: string
  /** `stale_after_hours` the club's; `default_stale_after_hours` the kind's (M24 review: « (à confirmer) » only while they are equal, rule 12) */
  connector: { connector_id: string; kind: string; external_ref: string; point_label: string; enabled: boolean; stale_after_hours: number; default_stale_after_hours: number }
  source: { kind_label: ForecastWords; provider_name: string; attribution: ForecastWords; derived_attribution: ForecastWords; licence: string | null; licence_url: string | null }
  issue: {
    issue_id: string; provider: string; issuer: string; product: string; generating_process: string; models: Array<{ name: string; reference_time: string }>
    point_code: string; point_name: string; point_lat: number; point_lon: number; point_height_m: number
    /** from the point to the course's reference point (the centroid of its shapes in force); null without a shape ("distance inconnue") */
    distance_km: number | null
    /** the direction of the point SEEN FROM THE COURSE, degrees clockwise from north (0-359); null without a shape */
    bearing_deg: number | null
    issued_at: string; received_at: string; first_step_at: string; step_minutes: number; step_count: number
    attribution: ForecastWords; licence: string | null; licence_url: string | null; raw_sha256: string
  }
  /** the issue's age when read, and whether it is older than the connector's `stale_after` ("ancienne") */
  freshness: { age_minutes: number; stale: boolean }
  /** the newest attempt of the job for this connector, whatever its outcome (null before the first) */
  last_run: { outcome: string; finished_at: string; error_code: string | null } | null
  elements: ForecastElementSeries[]
}
